import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { CTAButton } from "./CTAButton";
import { HeroVisual } from "./HeroVisual";
import { EASE } from "@/lib/site";
import { goToContact, scrollToId } from "@/lib/scroll";

const LINES = [
  [{ t: "Transformamos" }],
  [{ t: "ideias em " }, { t: "soluções", hl: true }],
  [{ t: "digitais." }],
];

const MaskedLine = ({ parts, i }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "115%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.13, ease: EASE }}
    >
      {parts.map((p) => (
        <span key={p.t} className={p.hl ? "text-ws-lime text-glow" : ""}>
          {p.t}
        </span>
      ))}
    </motion.span>
  </span>
);

const fade = (d) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: d, ease: EASE },
});

export const Hero = () => (
  <section id="inicio" data-testid="hero-section" className="relative overflow-hidden pb-16 pt-32 sm:pt-36 lg:pb-24 lg:pt-40">
    <div className="line-grid mask-fade-b pointer-events-none absolute inset-0 opacity-70" />
    <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-ws-lime/[0.07] blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
      <div className="lg:col-span-7">
        <motion.p {...fade(0.2)} className="inline-flex items-center gap-2.5 rounded-full border border-ws-lime/25 bg-ws-lime/[0.06] px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ws-glow sm:text-xs">
          <span className="relative flex h-2 w-2">
            <span className="pulse-ring absolute inset-0 rounded-full bg-ws-lime" />
            <span className="relative h-2 w-2 rounded-full bg-ws-lime" />
          </span>
          Desde 2020 · Sites · Apps · Sistemas
        </motion.p>
        <h1 data-testid="hero-headline" className="mt-7 font-display text-[2.05rem] font-semibold leading-[1.04] tracking-[-0.03em] text-ws-text min-[400px]:text-[2.3rem] sm:text-6xl lg:text-[3.2rem] xl:text-[3.9rem]">
          {LINES.map((parts, i) => (
            <MaskedLine key={i} parts={parts} i={i} />
          ))}
        </h1>
        <motion.p {...fade(0.85)} className="mt-7 max-w-xl text-lg leading-relaxed text-ws-text/90 sm:text-xl">
          Sites, aplicativos e sistemas sob medida para levar o seu negócio para o próximo nível.
        </motion.p>
        <motion.p {...fade(0.95)} className="mt-4 max-w-xl text-base leading-relaxed text-ws-muted">
          Do profissional autônomo à empresa em crescimento, criamos tecnologia sob medida para o seu negócio.
        </motion.p>
        <motion.div {...fade(1.05)} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <CTAButton testid="hero-cta-primary" onClick={() => goToContact()}>
            Quero criar meu projeto
          </CTAButton>
          <CTAButton testid="hero-cta-secondary" variant="ghost" icon={false} onClick={() => scrollToId("servicos")}>
            Conhecer nossos serviços
            <ArrowDown size={16} className="ml-1 inline transition-transform group-hover:translate-y-0.5" />
          </CTAButton>
        </motion.div>
        <motion.p {...fade(1.2)} className="mt-10 flex items-center gap-3 font-mono text-xs text-ws-muted">
          <span className="h-px w-10 bg-ws-lime/50" />
          WebStation — tecnologia criada para o seu negócio.
        </motion.p>
      </div>
      <div className="lg:col-span-5">
        <HeroVisual />
      </div>
    </div>
  </section>
);
